import { useCallback, useRef } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import { useTranslation } from 'react-i18next';
import type {
  ComparisonRequest,
  CreateRunRequest,
  RunComparison,
  RunRefusal,
} from '@/api/runs';
import { preferencesStore } from '@/features/preferences';
import type { PreferencesStore } from '@/features/preferences/preferencesStore';
import { generateRandomKey } from '@/utils/random';
import type { chatModeType } from '../../hooks/useHandleChatThinkingMode';
import { runStore } from '../../runs/runStore';
import type { ConvMessage } from '../../types';

/** A new key per submission (§3.1); resends of the same one reuse it. */
export const newIdempotencyKey = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // Not a secure context.
  }
  return `${generateRandomKey()}-${generateRandomKey()}`;
};

/**
 * The last submission per Q&A that got no answer (a network error or a
 * proxy's 5xx), with its key: sending the same thing again soon after
 * reuses the key, so if the first POST did create a run the server returns
 * that run instead of a second question (§3.1). The key is not reused once
 * the run shows up in the Q&A (the POST did arrive: the same submission
 * again is a new one), after UNANSWERED_KEY_MS (the run store's window for
 * claiming such a run), or after any other outcome.
 */
const unansweredSends = new Map<
  string,
  { fingerprint: string; key: string; at: number; runIds: Set<string> }
>();

const UNANSWERED_KEY_MS = 120_000;

/** What identifies a submission, without the send time that changes each try. */
const fingerprintOf = (
  body: Omit<CreateRunRequest, 'idempotency_key'>,
): string => JSON.stringify({ ...body, client_sent_at: undefined });

/**
 * The idempotency key for a submission: the unanswered one's when the same
 * submission is sent again in that Q&A, else a new one.
 */
export const keyForSubmission = (
  inquiryId: string,
  body: Omit<CreateRunRequest, 'idempotency_key'>,
  /** The runs the Q&A shows now. */
  runIds: readonly string[] = [],
  now = Date.now(),
) => {
  const unanswered = unansweredSends.get(inquiryId);
  const reusable =
    !!unanswered &&
    unanswered.fingerprint === fingerprintOf(body) &&
    now - unanswered.at < UNANSWERED_KEY_MS &&
    runIds.every(runId => unanswered.runIds.has(runId));
  return reusable ? unanswered.key : newIdempotencyKey();
};

/** Remember (network error) or forget (any other outcome) a submission's key. */
export const noteSubmissionOutcome = (
  inquiryId: string,
  body: Omit<CreateRunRequest, 'idempotency_key'>,
  key: string,
  unanswered: boolean,
  /** The runs the Q&A showed when the POST went out. */
  runIds: readonly string[] = [],
  now = Date.now(),
) => {
  if (unanswered) {
    unansweredSends.set(inquiryId, {
      fingerprint: fingerprintOf(body),
      key,
      at: now,
      runIds: new Set(runIds),
    });
  } else {
    unansweredSends.delete(inquiryId);
  }
};

/** The answer language for `system_language` (§3.1). */
export const systemLanguageOf = (language: string): 'english' | 'japanese' =>
  language.toLowerCase().startsWith('en') ? 'english' : 'japanese';

/** How long a send waits for the user's stored preferences (D-24). */
export const PREFERENCES_WAIT_MS = 5_000;

/**
 * The answer language of a submission: the user's language preference
 * (spec §2: answers follow the UI language, and the UI follows the
 * preference). Read from the UI language alone, a question sent right after
 * the page opened went out as `japanese` while the server preference `en`
 * was still loading; the UI switched to English a moment later (D-24). So
 * this waits for the first preferences load to settle, then uses the stored
 * language, and only without one (no user, or nothing loaded or cached)
 * the UI's.
 */
export const answerLanguage = async (
  store: Pick<PreferencesStore, 'whenLoaded' | 'getState'>,
  uiLanguage: () => string,
  waitMs = PREFERENCES_WAIT_MS,
): Promise<'english' | 'japanese'> => {
  await store.whenLoaded(waitMs);
  const { userKey, source, prefs } = store.getState();
  if (userKey != null && source !== 'default') {
    return prefs.language === 'en' ? 'english' : 'japanese';
  }
  return systemLanguageOf(uiLanguage());
};

/** The roles a comparison was frozen with, to send again with an edit. */
export const comparisonFromRecord = (
  comparison: RunComparison | null | undefined,
): ComparisonRequest | undefined => {
  if (!comparison) return undefined;
  if (comparison.purpose === 'changes' && comparison.baseline) {
    return { purpose: 'changes', baseline_file_id: comparison.baseline.id };
  }
  if (comparison.purpose === 'guidelines' && comparison.document_to_check) {
    return {
      purpose: 'guidelines',
      document_to_check_id: comparison.document_to_check.id,
    };
  }
  return undefined;
};

/** The text to show for a refusal of a new question or an edit. */
export const refusalText = (refusal: RunRefusal): string | undefined => {
  if (refusal.message && !refusal.errors) return refusal.message;
  const first = Object.values(refusal.errors ?? {})[0]?.[0];
  return first ?? refusal.message;
};

interface Options {
  inquiryId: string | undefined;
  mode: chatModeType;
  /**
   * The purpose and roles an edited comparison is sent with (the ones
   * chosen in the composer, else the question's own), or undefined when
   * none fits the current selection.
   */
  editComparison: (message: ConvMessage) => ComparisonRequest | undefined;
  /** The record a pending comparison shows until the list is loaded again. */
  freezeComparison: (request: ComparisonRequest) => RunComparison;
  addPending: (inquiryId: string, message: ConvMessage) => void;
  reload: () => Promise<void>;
  /** A question was accepted by the server. */
  onSent: (inquiryId: string) => void;
  messageApi: MessageInstance;
  /**
   * The runs the Q&A shows now: a run that appears after a submission got
   * no answer means that POST arrived, so its key is not reused.
   */
  runIds?: () => string[];
}

type Outcome = { ok: true } | { ok: false; refusal: RunRefusal | null };

/**
 * Sending questions and resubmissions as chat runs, and Stop. Every
 * submission goes through the run store, which refuses a second one while
 * the Q&A is busy; the server's 409 and 422 answers come back here.
 */
export const useRunActions = ({
  inquiryId,
  mode,
  editComparison,
  freezeComparison,
  addPending,
  reload,
  onSent,
  messageApi,
  runIds = () => [],
}: Options) => {
  const { t, i18n } = useTranslation();
  const latest = useRef({
    inquiryId,
    mode,
    editComparison,
    freezeComparison,
    onSent,
    reload,
    addPending,
    runIds,
  });
  latest.current = {
    inquiryId,
    mode,
    editComparison,
    freezeComparison,
    onSent,
    reload,
    addPending,
    runIds,
  };

  const submit = useCallback(
    async (
      body: Omit<CreateRunRequest, 'inquiry_id' | 'idempotency_key'>,
      question: string,
      /** The mode the server will use when `body.mode` is omitted. */
      parentMode: string | null = null,
      /** A comparison's frozen purpose and roles, for the pending question. */
      frozen: RunComparison | null = null,
    ): Promise<Outcome> => {
      const id = latest.current.inquiryId;
      if (!id) return { ok: false, refusal: null };
      const request = {
        ...body,
        inquiry_id: Number(id),
        system_language: await answerLanguage(
          preferencesStore,
          () => i18n.language,
        ),
      };
      const shown = latest.current.runIds();
      const key = keyForSubmission(id, request, shown);
      const result = await runStore.submit(id, request, key);
      // A local refusal (another run is busy) says nothing about the POST.
      // No answer, or a proxy's 5xx: the run may exist all the same.
      if (result.kind !== 'busy') {
        noteSubmissionOutcome(
          id,
          request,
          key,
          result.kind === 'network' ||
            (result.kind === 'error' && result.status >= 500),
          shown,
        );
      }
      switch (result.kind) {
        case 'created': {
          const now = new Date().toISOString();
          latest.current.addPending(id, {
            id: result.messageId,
            message: question,
            response: '',
            run_id: result.runId,
            run_status: result.status,
            mode: (body.mode ?? parentMode) as ConvMessage['mode'],
            kind: body.parent?.kind ?? null,
            parent_message_id: body.parent?.message_id ?? null,
            format: 'html',
            comparison: frozen,
            status: '',
            sender_id: '',
            chat_session_id: null,
            document_ids: [],
            created_at: now,
            updated_at: now,
          });
          latest.current.onSent(id);
          void latest.current.reload();
          return { ok: true };
        }
        case 'busy':
          messageApi.warning(t('v2.messages.runActive'));
          return { ok: false, refusal: null };
        case 'conflict':
          // The store follows the active run; show its question too.
          messageApi.warning(t('v2.messages.runActive'));
          void latest.current.reload();
          return { ok: false, refusal: null };
        case 'rejected':
          return { ok: false, refusal: result.body };
        case 'network':
          // The run may exist although no answer arrived: the list shows
          // it, and picks up its run if it is active.
          messageApi.error(t('v2.messages.sendFailed'));
          void latest.current.reload();
          return { ok: false, refusal: null };
        default:
          messageApi.error(result.body?.message || t('v2.messages.sendFailed'));
          return { ok: false, refusal: null };
      }
    },
    [i18n, messageApi, t],
  );

  /** A new question; resolves to whether it was accepted. */
  const send = useCallback(
    async (
      text: string,
      comparison?: ComparisonRequest,
      options: {
        /** Leave documents still processing out (§3.1). */
        sendReadyOnly?: boolean;
        /** When Send was pressed, on the drafts' clock. */
        clientSentAt?: string;
        /** The purpose and roles to show until the list is reloaded. */
        frozen?: RunComparison;
      } = {},
    ) => {
      const outcome = await submit(
        {
          query: text,
          mode: latest.current.mode,
          ...(comparison ? { comparison } : {}),
          ...(options.sendReadyOnly ? { send_ready_only: true } : {}),
          ...(options.clientSentAt
            ? { client_sent_at: options.clientSentAt }
            : {}),
        },
        text,
        null,
        options.frozen ?? null,
      );
      if (!outcome.ok && outcome.refusal) {
        messageApi.error(
          refusalText(outcome.refusal) ?? t('v2.messages.sendFailed'),
        );
      }
      return outcome.ok;
    },
    [messageApi, submit, t],
  );

  /**
   * Retry or Create another answer: the parent's question, mode, prompt and
   * sources (the server's defaults). A parent without a recorded mode gets
   * the mode chosen now. Resolves to the refusal, if any.
   */
  const resubmitParent = useCallback(
    async (kind: 'retry' | 'alternative', message: ConvMessage) => {
      const outcome = await submit(
        {
          parent: { kind, message_id: Number(message.id) },
          ...(message.mode ? {} : { mode: latest.current.mode }),
        },
        message.message,
        message.mode ?? null,
      );
      return outcome.ok ? null : outcome.refusal;
    },
    [submit],
  );

  const retry = useCallback(
    (message: ConvMessage) => resubmitParent('retry', message),
    [resubmitParent],
  );
  const alternative = useCallback(
    (message: ConvMessage) => resubmitParent('alternative', message),
    [resubmitParent],
  );

  /** Edit and resubmit: the edited question with the current sources. */
  const resubmitEdited = useCallback(
    async (message: ConvMessage, text: string) => {
      const editMode = message.mode ?? latest.current.mode;
      const comparison =
        editMode === 'comparison'
          ? latest.current.editComparison(message)
          : undefined;
      if (editMode === 'comparison' && !comparison) {
        messageApi.warning(t('v2.composer.rBaseline'));
        return false;
      }
      const outcome = await submit(
        {
          query: text,
          mode: editMode,
          parent: { kind: 'edit', message_id: Number(message.id) },
          ...(comparison ? { comparison } : {}),
        },
        text,
        null,
        // Its roles show at once, like a new question's.
        comparison ? latest.current.freezeComparison(comparison) : null,
      );
      if (!outcome.ok && outcome.refusal) {
        messageApi.error(
          refusalText(outcome.refusal) ?? t('v2.messages.sendFailed'),
        );
      }
      return outcome.ok;
    },
    [messageApi, submit, t],
  );

  const stop = useCallback((runId: string) => {
    void runStore.stop(runId, latest.current.inquiryId);
  }, []);

  const reconnect = useCallback((runId: string) => {
    void runStore.reconnect(runId);
  }, []);

  return { send, retry, alternative, resubmitEdited, stop, reconnect };
};
